import { GenderEnum, PhoneTypeEnum } from '.spec/self-care/tsTypes';
import { format } from 'date-fns';
import React from 'react';

import { MyProfile } from '@skippay/core/@types/myProfile';
import Button from '@skippay/core/components/Button';
import FormattedPhone from '@skippay/core/components/FormattedPhone';
import { usePageview } from '@skippay/core/components/GoogleAnalytics';
import Text from '@skippay/core/components/Text';
import { isoCountries } from '@skippay/core/constants/countryCodes';
import useInitialCountryLocale from '@skippay/core/hooks/useInitialCountryLocale';
import { FormattedMessage } from '@skippay/core/i18n/intl';
import { addSlashToPersonalId } from '@skippay/core/utils/personalId';

import { FooterButtons } from 'layout/MinimalLayout/components/FooterButtons';
import HeaderSection from 'layout/MinimalLayout/components/HeaderSection';
import ConsentNotice from 'screens/AccountUpgrade/components/ConsentNotice/ConsentNotice';
import {
	getIdentityDocumentNumberLabelMessage,
	getIdentityDocumentValidityLabelMessage,
} from 'utils/identityDocument';

import { BnplLimitSetupKycData } from '../../types';

type PhoneNumber = {
	countryCode: string;
	number: string;
	phoneType: PhoneTypeEnum;
};

type RowProps = {
	label: React.ReactNode;
	value?: React.ReactNode;
};

const Row = ({ label, value }: RowProps) => (
	<div className="flex flex-row items-center justify-between gap-4">
		<Text as="span" size="paragraph">
			{label}
		</Text>
		<Text as="strong" size="paragraph">
			{value}
		</Text>
	</div>
);

type SectionProps = {
	title?: React.ReactNode;
	children: React.ReactNode;
};

const Section = ({ title, children }: SectionProps) => (
	<div>
		{title && (
			<Text as="h3" align="center" className="mb-4!">
				{title}
			</Text>
		)}
		<div className="dark:bg-d-blue-100! flex flex-col gap-2 rounded-lg! bg-blue-50! p-4! text-left">
			{children}
		</div>
	</div>
);

const DATE_FORMAT = 'dd/MM/yyyy';

interface Props {
	kycData?: BnplLimitSetupKycData | null;
	phoneNumbers?: MyProfile['phoneNumbers'];
	isConfirming: boolean;
	onConfirm: () => void;
	onDecline: () => void;
}

function KycDataConfirmation(props: Props): JSX.Element {
	const { kycData, phoneNumbers, isConfirming, onConfirm, onDecline } = props;
	const { locale } = useInitialCountryLocale();
	usePageview({ pTitle: 'bnpl_limit_management_kyc_data_confirmation' });

	const permanentAddress = kycData?.addresses?.[0];
	const isCZNationality = kycData?.nationality === 'CZ';
	const primaryPhone = phoneNumbers?.find(({ phoneType }) => phoneType === PhoneTypeEnum.PRIMARY);

	return (
		<section className="mx-auto max-w-[640px] px-4 pt-6 pb-8">
			<div className="flex flex-col gap-8">
				<HeaderSection
					title={
						<FormattedMessage
							id="BnplLimitManagement.KycDataConfirmation.title"
							defaultMessage="Jsou vaše údaje stále aktuální?"
						/>
					}
				/>

				<Section
					title={
						<FormattedMessage
							id="BnplLimitManagement.KycDataConfirmation.personal"
							defaultMessage="Osobní údaje"
						/>
					}
				>
					<Row
						label={
							<FormattedMessage
								defaultMessage="Jméno"
								id="BnplLimitManagement.KycDataConfirmation.firstName"
							/>
						}
						value={kycData?.firstName}
					/>
					<Row
						label={
							<FormattedMessage
								defaultMessage="Příjmení"
								id="BnplLimitManagement.KycDataConfirmation.lastName"
							/>
						}
						value={kycData?.lastName}
					/>
					{kycData?.nationality && (
						<Row
							label={
								<FormattedMessage
									defaultMessage="Státní příslušnost"
									id="BnplLimitManagement.KycDataConfirmation.nationality"
								/>
							}
							value={isoCountries[locale][kycData.nationality]}
						/>
					)}
					{permanentAddress && (
						<Row
							label={
								isCZNationality ? (
									<FormattedMessage
										defaultMessage="Adresa trvalého pobytu"
										id="BnplLimitManagement.KycDataConfirmation.permanentAddress"
									/>
								) : (
									<FormattedMessage
										defaultMessage="Adresa"
										id="BnplLimitManagement.KycDataConfirmation.address"
									/>
								)
							}
							value={`${permanentAddress.streetAddress} ${permanentAddress.streetNumber}, ${permanentAddress.zip} ${permanentAddress.city}`}
						/>
					)}
					{isCZNationality ? (
						<Row
							label={
								<FormattedMessage
									defaultMessage="Rodné číslo"
									id="BnplLimitManagement.KycDataConfirmation.rc"
								/>
							}
							value={addSlashToPersonalId(kycData?.personalId ?? undefined)}
						/>
					) : (
						<>
							{kycData?.dateOfBirth && (
								<Row
									label={
										<FormattedMessage
											defaultMessage="Datum narození"
											id="BnplLimitManagement.KycDataConfirmation.dateOfBirth"
										/>
									}
									value={format(new Date(kycData.dateOfBirth), DATE_FORMAT)}
								/>
							)}
							{kycData?.placeOfBirth && (
								<Row
									label={
										<FormattedMessage
											defaultMessage="Země narození"
											id="BnplLimitManagement.KycDataConfirmation.placeOfBirth"
										/>
									}
									value={isoCountries[locale][kycData.placeOfBirth]}
								/>
							)}
							{kycData?.gender && (
								<Row
									label={
										<FormattedMessage
											defaultMessage="Pohlaví"
											id="BnplLimitManagement.KycDataConfirmation.gender"
										/>
									}
									value={
										kycData.gender === GenderEnum.MALE ? (
											<FormattedMessage
												defaultMessage="Muž"
												id="BnplLimitManagement.KycDataConfirmation.gender.male"
											/>
										) : (
											<FormattedMessage
												defaultMessage="Žena"
												id="BnplLimitManagement.KycDataConfirmation.gender.female"
											/>
										)
									}
								/>
							)}
						</>
					)}
				</Section>

				{kycData?.identityDocument && (
					<Section
						title={
							<FormattedMessage
								id="BnplLimitManagement.KycDataConfirmation.document"
								defaultMessage="Doklad"
							/>
						}
					>
						<Row
							label={
								<FormattedMessage
									{...getIdentityDocumentNumberLabelMessage(kycData.identityDocument.type)}
								/>
							}
							value={kycData.identityDocument.number}
						/>
						<Row
							label={
								<FormattedMessage
									{...getIdentityDocumentValidityLabelMessage(kycData.identityDocument.type)}
								/>
							}
							value={format(new Date(kycData.identityDocument.validity), DATE_FORMAT)}
						/>
					</Section>
				)}

				{primaryPhone && (
					<Section
						title={
							<FormattedMessage
								id="BnplLimitManagement.KycDataConfirmation.contact"
								defaultMessage="Kontaktní údaje"
							/>
						}
					>
						<Row
							label={
								<FormattedMessage
									defaultMessage="Telefonní číslo"
									id="BnplLimitManagement.KycDataConfirmation.phone"
								/>
							}
							value={<FormattedPhone {...(primaryPhone as PhoneNumber)} />}
						/>
					</Section>
				)}

				<ConsentNotice />
			</div>

			<FooterButtons
				leftContent={
					<Button
						analyticsId="bnplDeclineKycData"
						onClick={onDecline}
						testId="bnplDeclineKycData"
						type="button"
						variant="info"
						width="full"
						disabled={isConfirming}
					>
						<FormattedMessage
							id="BnplLimitManagement.KycDataConfirmation.decline"
							defaultMessage="Údaje nesedí"
						/>
					</Button>
				}
				content={
					<Button
						analyticsId="bnplConfirmKycData"
						onClick={onConfirm}
						testId="bnplConfirmKycData"
						width="full"
						disabled={isConfirming}
					>
						<FormattedMessage
							id="BnplLimitManagement.KycDataConfirmation.submit"
							defaultMessage="Potvrdit"
						/>
					</Button>
				}
			/>
		</section>
	);
}

export default KycDataConfirmation;
